/**
 * Development Domain Processor Utility
 *
 * Handles domain processing specifically for local development environment.
 * When using SIT or UAT configurations locally, domains need to be adjusted
 * to work with localhost while maintaining the country-specific structure.
 */

import { isDevelopment } from '../environment'

interface DomainProcessingOptions {
  countryCode: string
  originalDomain: string
}

export function isDevelopmentEnvironment(): boolean {
  // Check both STAGE and NODE_ENV for comprehensive development detection
  const stage = process.env.STAGE || 'dev'
  return isDevelopment || stage === 'dev'
}

export function removeProtocolFromDomain(domain: string): string {
  // Remove common protocols (http://, https://, ftp://, etc.)
  return domain.replace(/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//, '')
}

export function hasLocalhostReference(domain: string): boolean {
  const cleanDomain = removeProtocolFromDomain(domain).toLowerCase()
  return cleanDomain.includes('localhost') || cleanDomain.includes('127.0.0.1')
}

export function processDomain(options: DomainProcessingOptions): string {
  const { countryCode, originalDomain } = options

  const cleanDomain = removeProtocolFromDomain(originalDomain)

  if (!isDevelopmentEnvironment()) {
    return cleanDomain
  }

  // If domain already contains localhost, keep it as-is
  if (hasLocalhostReference(cleanDomain)) {
    return cleanDomain
  }

  // Replace with country-specific localhost domain
  const developmentDomain = `${countryCode.toLowerCase()}.localhost`

  console.info('🔄 [Dev] Domain transformed for local development:', {
    original: originalDomain,
    cleaned: cleanDomain,
    transformed: developmentDomain,
    country: countryCode
  })

  return developmentDomain
}

/**
 * Batch process multiple domains for development environment
 */
export function processDevelopmentDomains(
  domainsData: Record<string, { domain: string }>
): Record<string, string> {
  const processedDomains: Record<string, string> = {}

  Object.entries(domainsData).forEach(([countryCode, countryConfig]) => {
    const processedDomain = processDomain({
      countryCode,
      originalDomain: countryConfig.domain
    })

    processedDomains[processedDomain] = countryCode
  })

  return processedDomains
}
